import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import GppMaybeRoundedIcon from '@mui/icons-material/GppMaybeRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import { Box, ButtonBase, Paper, Skeleton, Stack, Typography } from '@mui/material';

export interface AccessReviewSummaryProps {
  /** Departments conferring anything at all. */
  grantingDepartments: number;
  totalDepartments: number;
  /** Departments carrying a high-risk capability, ADMIN, or rights with nobody in them. */
  flaggedDepartments: number;
  /** Flagged employees among the page currently loaded — the listing is server-paged. */
  flaggedPeople: number;
  peopleOnPage: number;
  loading: boolean;
  onOpenDepartments: (flaggedOnly: boolean) => void;
  onOpenPeople: (flaggedOnly: boolean) => void;
}

/**
 * What needs a look, before the listings.
 *
 * <p>A periodic access review is a task, not a dashboard: the operator arrives wanting to know what looks
 * wrong, and leaves wanting to know what is left. Two long tables answered neither question — with a
 * handful of departments and people they were readable, and with a realistic company they were two
 * competing walls of rows with the interesting three hidden somewhere inside.
 *
 * <p>So the counts come first and every one of them is a way in: each tile jumps to the listing it
 * describes with the matching filter applied, which is what turns a number into the next action. The
 * counts are derived from the same rules the row markers use ({@link ../../lib/reviewFlags}), so the
 * summary can never claim something the rows below it do not show.
 *
 * <p>The people figure is deliberately scoped to the loaded page and says so: the review endpoint is
 * server-paged, and a tenant-wide "12 need attention" computed from 25 rows would be a number the screen
 * cannot stand behind.
 */
export function AccessReviewSummary({
  grantingDepartments,
  totalDepartments,
  flaggedDepartments,
  flaggedPeople,
  peopleOnPage,
  loading,
  onOpenDepartments,
  onOpenPeople,
}: AccessReviewSummaryProps) {
  const { t } = useTranslation('access');
  const allClear = !loading && flaggedDepartments === 0 && flaggedPeople === 0;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">{t('review.summary.title')}</Typography>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
            {allClear && (
              <CheckCircleRoundedIcon
                aria-hidden
                fontSize="inherit"
                sx={{ color: 'success.main' }}
              />
            )}
            <Typography variant="body2" color="text.secondary">
              {loading
                ? t('review.summary.hint')
                : allClear
                  ? t('review.summary.allClear')
                  : t('review.summary.remaining', {
                      departments: flaggedDepartments,
                      people: flaggedPeople,
                    })}
            </Typography>
          </Stack>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
          }}
        >
          <SummaryTile
            icon={<AccountTreeRoundedIcon />}
            label={t('review.summary.departments.label')}
            value={grantingDepartments}
            caption={t('review.summary.departments.caption', { count: totalDepartments })}
            loading={loading}
            onOpen={() => onOpenDepartments(false)}
          />
          <SummaryTile
            icon={<GppMaybeRoundedIcon />}
            label={t('review.summary.attention.label')}
            value={flaggedDepartments}
            caption={t('review.summary.attention.caption')}
            warning={flaggedDepartments > 0}
            loading={loading}
            onOpen={() => onOpenDepartments(true)}
          />
          <SummaryTile
            icon={<PeopleAltRoundedIcon />}
            label={t('review.summary.people.label')}
            value={flaggedPeople}
            caption={t('review.summary.people.caption', { count: peopleOnPage })}
            warning={flaggedPeople > 0}
            loading={loading}
            onOpen={() => onOpenPeople(true)}
          />
        </Box>
      </Stack>
    </Paper>
  );
}

interface SummaryTileProps {
  icon: ReactNode;
  label: string;
  value: number;
  caption: string;
  warning?: boolean;
  loading: boolean;
  onOpen: () => void;
}

/**
 * One count, and the way into the rows behind it.
 *
 * <p>Emphasis is never colour alone: a tile that needs attention gets a warning border *and* a warning
 * icon *and* a caption that names the rule, so it reads the same to someone who cannot distinguish the
 * border colour.
 */
function SummaryTile({ icon, label, value, caption, warning, loading, onOpen }: SummaryTileProps) {
  return (
    <ButtonBase
      focusRipple
      onClick={onOpen}
      disabled={loading}
      aria-label={`${label}: ${value}`}
      sx={{
        justifyContent: 'stretch',
        textAlign: 'left',
        border: 1,
        borderColor: warning ? 'warning.main' : 'divider',
        borderRadius: 1.5,
        p: 1.5,
        bgcolor: 'background.paper',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
        <Box
          aria-hidden
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            bgcolor: 'action.hover',
            color: warning ? 'warning.main' : 'primary.main',
            '& svg': { fontSize: 20 },
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.4, letterSpacing: '0.08em' }}
          >
            {label}
          </Typography>
          {loading ? (
            <Skeleton variant="text" width={48} />
          ) : (
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: warning ? 'warning.main' : 'text.primary' }}
            >
              {value}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {caption}
          </Typography>
        </Box>
        <ChevronRightRoundedIcon aria-hidden sx={{ color: 'text.disabled', flexShrink: 0 }} />
      </Stack>
    </ButtonBase>
  );
}
